import { Tab, Tabs } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Spinner from 'components/shared/Spinner';
import { UserContext } from 'components/UserGuard';
import CompanyPageView from 'components/company/CompanyPageView';
import CompanySelect from 'components/company/CompanySelect';
import TabContext from '@mui/lab/TabContext';
import { CompanySelectOption, getCompanySelectOptions } from 'util/Company';
import TabPanel from '@mui/lab/TabPanel';
import { useHistory, useLocation } from 'react-router';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import routes from 'store/configs/Routes';
import CompanyDetailsPage from 'components/company/CompanyDetailsPage';
import CompanyAddressesPage from 'components/company/CompanyAddressesPage';
import CompanyRequestsPage from 'components/company/CompanyRequestsPage';
import { ParametersContext } from 'components/ParametersGuard';
import { ConfigContext } from 'components/ConfigGuard';
import SiteModule from 'store/enums/SiteModule';
import CountBadge from 'components/shared/CountBadge';
import CompanyService from 'services/api/CompanyService';
import { CompanyRequestsInfo } from 'store/types/Company';
import useRequest from 'hooks/useRequest';
import CompanyAdvocacyPage from 'components/company/CompanyAdvocacyPage';
import { hasMemberType, isModuleAvailable } from 'util/User';
import classNames from 'classnames';
import CompanyApplicationsPage from 'components/company/CompanyApplicationsPage';
import { TabItem } from 'store/types/Tabs';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';

import commonStyles from 'styles/common.module.scss';
import styles from './CompanyPage.module.scss';

const tabConfig = {
  CompanyManagement: {
    key: 'CompanyManagement',
    label: 'Company Management',
    route: routes.companyManagement,
  } as TabItem,
  CompanyInformation: {
    key: 'CompanyInformation',
    label: 'Company Information',
    route: routes.companyInformation,
  } as TabItem,
  CompanyAddresses: {
    key: 'CompanyAddresses',
    label: 'Company Addresses',
    route: routes.companyAddresses,
  } as TabItem,
  CompanyRequests: {
    key: 'CompanyRequests',
    label: 'Employee Requests',
    route: routes.companyRequests,
  } as TabItem,
  CompanyApplications: {
    key: 'CompanyApplications',
    label: 'Compliance & Change Requests',
    route: routes.companyApplications,
  } as TabItem,
  CompanyAdvocacy: {
    key: 'CompanyAdvocacy',
    label: 'Advocacy',
    route: routes.companyAdvocacy,
  } as TabItem,
};

const CompanyPage: React.FunctionComponent = () => {
  const { functions = [], memberTypeName, userLoading, userPermissions } = useContext(UserContext);
  const history = useHistory();
  const {
    companyManagement: {
      companyMgtHeader,
      companyAddressHeader,
      companyInformationHeader,
      companyAdvocacyHeader,
      companyApplicationsHeader,
      companyRequestsHeader,
    },
  } = useContext(ParametersContext);
  const {
    enabledModules = [],
    isBayViewTheme,
    modulesSettings: {
      companyManagement: { advocacy, enableAdvocacy, hiddenTabs },
    },
  } = useContext(ConfigContext);
  const { pathname } = useLocation();
  const [selectedCompany, setSelectedCompany] = useState<CompanySelectOption | undefined>(undefined);
  const companyEmployeeRequestsCountRequest = useMemo(
    () => (selectedCompany?.id ? () => CompanyService.getCompanyRequestsCount(selectedCompany.id) : undefined),
    [selectedCompany]
  );
  const { data, refetch } = useRequest<CompanyRequestsInfo>(companyEmployeeRequestsCountRequest);

  const companyManagementTabLabel = useMemo(
    () => (isBayViewTheme ? 'Household Management' : tabConfig.CompanyManagement.label),
    [isBayViewTheme]
  );

  const companyInformationTabLabel = useMemo(
    () => (isBayViewTheme ? 'Household Information' : tabConfig.CompanyInformation.label),
    [isBayViewTheme]
  );

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const permissions: string[] = useMemo(
    () => userPermissions.modules.find(({ name }) => name === SiteModule.Company)?.permissions || [],
    [userPermissions]
  );
  const options: CompanySelectOption[] = useMemo(
    () => getCompanySelectOptions(functions, permissions),
    [functions, permissions]
  );
  const activeTab: string = useMemo(() => getTabKeyByRoute(pathname, tabConfig), [pathname]);

  const checkModuleAccess = useCallback(
    (module: SiteModule) => isModuleAvailable(module, functions, userPermissions),
    [functions, userPermissions]
  );

  const handleCompanyChange = useCallback(
    (newCompanyId: string) => {
      setSelectedCompany(options.find(({ id }) => id === newCompanyId) || undefined);
    },
    [options]
  );

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.CompanyManagement.key) => {
      history.replace(getTabRouteByKey(value, tabConfig));
    },
    [history]
  );

  useEffect(() => {
    history.replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [history, visibleTabs]);

  return (
    <TabContext value={activeTab}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor={'primary'}
        textColor={'primary'}
        TabIndicatorProps={{ sx: { display: 'none' } }}
        sx={{
          '& .MuiTabs-flexContainer': {
            flexWrap: 'wrap',
          },
        }}
      >
        {visibleTabs.map((tab) => {
          if (
            tab.key === tabConfig.CompanyApplications.key &&
            (!enabledModules.includes(SiteModule.Applications) || !checkModuleAccess(SiteModule.Applications))
          ) {
            return;
          }

          if (
            tab.key === tabConfig.CompanyAdvocacy.key &&
            (!enableAdvocacy || advocacy?.enable === false || !hasMemberType(memberTypeName, advocacy?.memberTypes))
          ) {
            return;
          }

          if (tab.key === tabConfig.CompanyRequests.key) {
            return (
              <Tab
                key={tabConfig.CompanyRequests.key}
                value={tabConfig.CompanyRequests.key}
                label={
                  <>
                    {tabConfig.CompanyRequests.label}
                    {!!data?.pending && <CountBadge className={styles.countBadge}>{data.pending}</CountBadge>}
                  </>
                }
                className={classNames(commonStyles.tab, styles.tabWithCounter)}
                classes={{ selected: commonStyles.activeTab }}
              />
            );
          }

          return (
            <Tab
              key={tab.key}
              value={tab.key}
              label={
                tab.key === tabConfig.CompanyManagement.key
                  ? companyManagementTabLabel
                  : tab.key === tabConfig.CompanyInformation.key
                  ? companyInformationTabLabel
                  : tab.label
              }
              className={commonStyles.tab}
              classes={{ selected: commonStyles.activeTab }}
            />
          );
        })}
      </Tabs>
      <div className={styles.companySelect}>
        {activeTab === tabConfig.CompanyManagement.key && companyMgtHeader ? (
          <p dangerouslySetInnerHTML={{ __html: companyMgtHeader }} />
        ) : activeTab === tabConfig.CompanyInformation.key && companyInformationHeader ? (
          <p dangerouslySetInnerHTML={{ __html: companyInformationHeader }} />
        ) : activeTab === tabConfig.CompanyAddresses.key && companyAddressHeader ? (
          <p dangerouslySetInnerHTML={{ __html: companyAddressHeader }} />
        ) : activeTab === tabConfig.CompanyRequests.key && companyRequestsHeader ? (
          <p dangerouslySetInnerHTML={{ __html: companyRequestsHeader }} />
        ) : activeTab === tabConfig.CompanyApplications.key && companyApplicationsHeader ? (
          <p dangerouslySetInnerHTML={{ __html: companyApplicationsHeader }} />
        ) : (
          activeTab === tabConfig.CompanyAdvocacy.key &&
          companyAdvocacyHeader && <p dangerouslySetInnerHTML={{ __html: companyAdvocacyHeader }} />
        )}
        <CompanySelect value={selectedCompany?.id} options={options} onChange={handleCompanyChange} size={'small'} />
      </div>
      <TabPanel value={tabConfig.CompanyManagement.key} className={commonStyles.tabPanel}>
        <Spinner loading={userLoading} transparent={false}>
          <CompanyPageView companyId={selectedCompany?.id || ''} refetchCount={refetch} />
        </Spinner>
      </TabPanel>
      <TabPanel value={tabConfig.CompanyInformation.key} className={commonStyles.tabPanel}>
        <Spinner loading={userLoading} transparent={false}>
          <CompanyDetailsPage companyId={selectedCompany?.id || ''} />
        </Spinner>
      </TabPanel>
      <TabPanel value={tabConfig.CompanyAddresses.key} className={commonStyles.tabPanel}>
        <Spinner loading={userLoading} transparent={false}>
          <CompanyAddressesPage selectedCompany={selectedCompany} />
        </Spinner>
      </TabPanel>
      <TabPanel value={tabConfig.CompanyRequests.key} className={commonStyles.tabPanel}>
        <Spinner loading={userLoading} transparent={false}>
          <CompanyRequestsPage companyId={selectedCompany?.id || ''} refetchCount={refetch} />
        </Spinner>
      </TabPanel>
      {enabledModules.includes(SiteModule.Applications) && checkModuleAccess(SiteModule.Applications) && (
        <TabPanel value={tabConfig.CompanyApplications.key} className={commonStyles.tabPanel}>
          <Spinner loading={userLoading} transparent={false}>
            <CompanyApplicationsPage companyId={selectedCompany?.id || ''} />
          </Spinner>
        </TabPanel>
      )}
      {enableAdvocacy && (
        <TabPanel value={tabConfig.CompanyAdvocacy.key} className={commonStyles.tabPanel}>
          <Spinner loading={userLoading} transparent={false}>
            <CompanyAdvocacyPage companyId={selectedCompany?.id || ''} />
          </Spinner>
        </TabPanel>
      )}
    </TabContext>
  );
};

export default CompanyPage;
